// The marks on a square. A mark is one or more bands at the edge of the
// square, drawn under the piece, so the middle stays clear and no mark crosses
// a badge. The bands go from solid at the edge to dither toward the middle,
// like the rings around the roll die. Three levels:
// - focus: the square in action. Solid, half dither, quarter dither.
// - choice: a square the player or the AI may pick. One solid band.
// - trace: where the last move was. One quarter band.
// A region is the focus bands around a block of squares.

import {
  CROSS_REACH_PX,
  CROSS_WIDTH_PX,
  DITHER_CELL_PX,
  MARK_BAND_CELLS,
  MARK_DASH_CELLS,
  type MARK_TONES,
  markDitherId,
  SQUARE_PX,
} from '../constants';
import { svg } from '../lib/svg';
import { boardOrigin } from './boardOrigin';

export type MarkTone = (typeof MARK_TONES)[number];

type Paint = 'solid' | 'half' | 'quarter';

const BAND_PX = DITHER_CELL_PX * MARK_BAND_CELLS;

type Box = { x: number; y: number; width: number; height: number };

/** Band 0 is at the edge of the box. Each next band is one band width nearer the middle. */
const bandOfBox = (
  box: Box,
  index: number,
  tone: MarkTone,
  paint: Paint,
  extra = '',
): SVGRectElement => {
  const inset = BAND_PX * (index + 0.5);
  const node = svg('rect', {
    class: `band band--${paint} band--${tone}${extra}`,
    x: box.x + inset,
    y: box.y + inset,
    width: box.width - inset * 2,
    height: box.height - inset * 2,
    'stroke-width': BAND_PX,
  });
  // A dither band takes its pattern by id. The pattern has the color of the tone.
  if (paint !== 'solid') node.style.stroke = `url("#${markDitherId(tone, paint)}")`;
  return node;
};

const boxOfSquare = (square: number): Box => ({
  ...boardOrigin(square),
  width: SQUARE_PX,
  height: SQUARE_PX,
});

/** The smallest box that holds every one of the squares. */
const boxOfSquares = (squares: readonly number[]): Box => {
  const origins = squares.map((square) => boardOrigin(square));
  const x = Math.min(...origins.map((origin) => origin.x));
  const y = Math.min(...origins.map((origin) => origin.y));
  return {
    x,
    y,
    width: Math.max(...origins.map((origin) => origin.x)) + SQUARE_PX - x,
    height: Math.max(...origins.map((origin) => origin.y)) + SQUARE_PX - y,
  };
};

const band = (
  square: number,
  index: number,
  tone: MarkTone,
  paint: Paint,
  extra = '',
): SVGRectElement => bandOfBox(boxOfSquare(square), index, tone, paint, extra);

/** The square in action: picked, lifted, changed by a spell. */
export const focusMark = (square: number, tone: MarkTone): SVGElement[] => [
  band(square, 0, tone, 'solid'),
  band(square, 1, tone, 'half'),
  band(square, 2, tone, 'quarter'),
];

/** A square that may be picked. `preview` is the faint form under a mouse. */
export const choiceMark = (
  square: number,
  tone: MarkTone,
  options: { dashed?: boolean; preview?: boolean } = {},
): SVGElement[] => {
  const node = band(square, 0, tone, 'solid', options.preview ? ' band--preview' : '');
  if (options.dashed) {
    const [on, off] = MARK_DASH_CELLS;
    node.setAttribute('stroke-dasharray', `${DITHER_CELL_PX * on} ${DITHER_CELL_PX * off}`);
  }
  return [node];
};

/** A target of the spell in the aim: a choice with one more band, so it differs from a capture. */
export const targetMark = (square: number, tone: MarkTone): SVGElement[] => [
  band(square, 0, tone, 'solid'),
  band(square, 1, tone, 'quarter'),
];

/** A region a pick takes: the focus bands around the whole block, not around each square. */
export const regionMark = (squares: readonly number[], tone: MarkTone): SVGElement[] => {
  if (squares.length === 0) return [];
  const box = boxOfSquares(squares);
  return [
    bandOfBox(box, 0, tone, 'solid'),
    bandOfBox(box, 1, tone, 'half'),
    bandOfBox(box, 2, tone, 'quarter'),
  ];
};

/** Where the last move was. */
export const traceMark = (square: number): SVGElement[] => [band(square, 0, 'trace', 'quarter')];

/** A cross over a piece that a pick removes. It is the one mark over the piece, because it must cross it. */
export const crossMark = (square: number): SVGPathElement => {
  const origin = boardOrigin(square);
  const x = origin.x + SQUARE_PX / 2;
  const y = origin.y + SQUARE_PX / 2;
  const reach = CROSS_REACH_PX;
  return svg('path', {
    class: 'cross',
    d: `M${x - reach} ${y - reach} L${x + reach} ${y + reach} M${x + reach} ${y - reach} L${x - reach} ${y + reach}`,
    'stroke-width': CROSS_WIDTH_PX,
  });
};
